import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Car,
  MapPin,
  Phone as PhoneIcon,
  Mail as MailIcon,
  User as UserIcon,
  ShieldCheck,
  CreditCard,
  ArrowLeft,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { businessInfo, payments as paymentMethods } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [opts, setOpts] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service_type: "",
    vehicle_year: "",
    vehicle_make: "",
    vehicle_model: "",
    preferred_date: "",
    time_window: "",
    location: "",
    notes: "",
  });

  useEffect(() => {
    axios
      .get(`${API}/booking-options`)
      .then((r) => setOpts(r.data))
      .catch(() => {});
  }, []);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const setField = (name, value) => setForm({ ...form, [name]: value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ["name", "phone", "email", "service_type"];
    for (const k of required) {
      if (!form[k]?.trim()) {
        toast({ title: "Missing info", description: `Please fill in your ${k.replace("_", " ")}.` });
        return;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast({ title: "Email looks off", description: "Please double-check your email address." });
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await axios.post(`${API}/bookings`, {
        ...form,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("no checkout url");
      }
    } catch (err) {
      toast({
        title: "Couldn't start checkout",
        description: "Please try again, or call " + businessInfo.phone + " to book.",
      });
      setSubmitting(false);
    }
  };

  const deposit = opts?.deposit_amount ?? 30;

  return (
    <div className="text-left bg-black text-white min-h-screen">
      <Header />
      <main className="pt-28 pb-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>

          <div className="mt-6">
            <span className="text-yellow-400 font-bold uppercase tracking-[0.3em] text-xs">Book a Service</span>
            <h1 className="mt-3 text-white font-extrabold text-3xl lg:text-5xl tracking-tight">
              Schedule with <span className="text-fire">Shane</span>
            </h1>
            <p className="mt-4 text-zinc-400 text-base lg:text-lg leading-relaxed">
              For non-emergency jobs. A small ${deposit.toFixed(2)} deposit reserves your time slot — it's applied to your final bill.
            </p>
            <p className="mt-2 text-zinc-500 text-sm">
              Locked out right now? <a href={`tel:${businessInfo.phoneRaw}`} className="text-red-400 hover:text-red-300 font-semibold">Call {businessInfo.phone}</a> for emergencies — no deposit needed.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-10 brand-card rounded-2xl p-6 lg:p-8 border-red-900/40 space-y-6">
            <Section title="Your Contact Info" icon={<UserIcon className="w-4 h-4" />}>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name">
                  <Input name="name" value={form.name} onChange={change} placeholder="Jane Doe" className="h-11 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600" />
                </Field>
                <Field label="Phone Number">
                  <Input name="phone" value={form.phone} onChange={change} placeholder="(606) 555-0123" className="h-11 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600" />
                </Field>
                <Field label="Email" className="sm:col-span-2">
                  <Input name="email" type="email" value={form.email} onChange={change} placeholder="you@example.com" className="h-11 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600" />
                </Field>
              </div>
            </Section>

            <Section title="Service Needed" icon={<ShieldCheck className="w-4 h-4" />}>
              <Field label="Service Type">
                <Select value={form.service_type} onValueChange={(v) => setField("service_type", v)}>
                  <SelectTrigger className="h-11 bg-zinc-900 border-zinc-700 text-white">
                    <SelectValue placeholder="Pick a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                    {(opts?.services || []).map((s) => (
                      <SelectItem key={s} value={s} className="text-white focus:bg-zinc-800 focus:text-white">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </Section>

            <Section title="Vehicle" icon={<Car className="w-4 h-4" />}>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Year">
                  <Input name="vehicle_year" value={form.vehicle_year} onChange={change} placeholder="2019" className="h-11 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600" />
                </Field>
                <Field label="Make">
                  <Input name="vehicle_make" value={form.vehicle_make} onChange={change} placeholder="Chevy" className="h-11 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600" />
                </Field>
                <Field label="Model">
                  <Input name="vehicle_model" value={form.vehicle_model} onChange={change} placeholder="Silverado" className="h-11 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600" />
                </Field>
              </div>
            </Section>

            <Section title="When & Where" icon={<Calendar className="w-4 h-4" />}>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Preferred Date">
                  <Input name="preferred_date" type="date" value={form.preferred_date} onChange={change} className="h-11 bg-zinc-900 border-zinc-700 text-white" />
                </Field>
                <Field label="Preferred Time">
                  <Select value={form.time_window} onValueChange={(v) => setField("time_window", v)}>
                    <SelectTrigger className="h-11 bg-zinc-900 border-zinc-700 text-white">
                      <SelectValue placeholder="Any time" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                      {(opts?.time_windows || []).map((t) => (
                        <SelectItem key={t} value={t} className="text-white focus:bg-zinc-800 focus:text-white">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Service Location" className="mt-4">
                <Input name="location" value={form.location} onChange={change} placeholder="Address or where you'll be (e.g. Walmart parking lot, Louisa)" className="h-11 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600" />
              </Field>
            </Section>

            <Section title="Anything Else?" icon={<MapPin className="w-4 h-4" />}>
              <Field label="Notes (optional)">
                <Textarea name="notes" value={form.notes} onChange={change} placeholder="Anything Shane should know…" className="min-h-[100px] bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600" />
              </Field>
            </Section>

            <div className="pt-2 border-t border-zinc-800">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-yellow-300 text-xs font-bold uppercase tracking-widest">Deposit Due</div>
                  <div className="text-white text-3xl font-extrabold mt-0.5">${deposit.toFixed(2)}</div>
                  <div className="text-zinc-500 text-xs mt-1">Applied to your final bill at the appointment.</div>
                </div>
                <Button type="submit" disabled={submitting} className="bg-brand-red text-white font-semibold h-12 px-7 text-base disabled:opacity-70">
                  <CreditCard className="w-4 h-4 mr-2" />
                  {submitting ? "Redirecting…" : `Pay $${deposit.toFixed(2)} & Book`}
                </Button>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {paymentMethods.map((p) => (
                  <span key={p} className="px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs">{p}</span>
                ))}
              </div>
              <p className="text-zinc-500 text-xs mt-3 leading-relaxed">
                Secure payment processed by Stripe. You'll be redirected to enter your card details. Need to call instead? <a href={`tel:${businessInfo.phoneRaw}`} className="text-red-400">{businessInfo.phone}</a>
              </p>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <div>
    <div className="flex items-center gap-2 text-red-400 text-xs font-bold uppercase tracking-widest mb-3">
      {icon}
      {title}
    </div>
    {children}
  </div>
);

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="text-sm font-semibold text-zinc-200">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export default BookingPage;
